import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    DashboardButtonService,
    MaterialService,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    notificationsReloaded$,
    notificationsUpdated$,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { DASHBOARD_TAB, DashboardModel } from './dashboard.model';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { TabsetComponent } from 'ngx-bootstrap/tabs';

@Component({
    selector: 'pace-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false,
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('tabSet', { static: false }) private tabSet: TabsetComponent;
    public model: DashboardModel;
    private destroy$ = new Subject<void>();
    public newAssignmentsCount$: Observable<number>;
    public newAssignmentsCountSubject: BehaviorSubject<number>;
    public activeTab$: Observable<DASHBOARD_TAB>;
    private activeTabSubject: BehaviorSubject<DASHBOARD_TAB>;
    /**
     * The notification counts have to be recomputed: after every completed reload, and after a
     * cancellation was confirmed. Captured here because `inject()` only runs during construction.
     */
    private readonly notificationCountOutdated$ = merge(
        notificationsReloaded$(),
        notificationsUpdated$().pipe(filter(Boolean)),
    );

    public strings = {
        todaysTitle: '',
        upcomingTitle: '',
        newTitle: '',
        todaysTitleOnSmartphone: '',
        upcomingTitleOnSmartphone: '',
        newTitleOnSmartphone: '',
    };
    public constructor(
        private readonly translate: TranslateService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly materialService: MaterialService,
        private readonly dashboardButtonService: DashboardButtonService,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        this.model = new DashboardModel();
        this.materialService.updateUnprocessedMaterials().pipe(take(1)).subscribe();
        this.newAssignmentsCountSubject = new BehaviorSubject<number>(0);
        this.newAssignmentsCount$ = this.newAssignmentsCountSubject.asObservable();
        this.activeTabSubject = new BehaviorSubject<DASHBOARD_TAB>(DASHBOARD_TAB.TODAYS);
        this.activeTab$ = this.activeTabSubject.asObservable();
    }

    public ngOnInit(): void {
        // show the no Equipment notification list on dashboard init
        this.notificationService.showNoEquipmentNotificationList(true);
        this.setActiveTab();
        this.dashboardButtonService.onRedirectToDashboard$
            .pipe(
                tap((value: boolean) => {
                    this.setActiveTab(value);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.strings.todaysTitle = <string>this.translate.instant('DASHBOARD_ASSIGNMENTS_FOR_TODAY_TITLE');
        this.strings.upcomingTitle = <string>this.translate.instant('DASHBOARD_UPCOMING_ASSIGNMENTS_TITLE');
        this.strings.newTitle = <string>this.translate.instant('DASHBOARD_NEW_ASSIGNMENTS_TITLE');
        this.updateNotificationCount();

        this.notificationCountOutdated$
            .pipe(
                tap(() => this.updateNotificationCount()),
                tap(() => {
                    this.setActiveTab();
                    this.changeDetectorRef.detectChanges();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public ngAfterViewInit(): void {
        this.activeTab$
            .pipe(
                takeUntil(this.destroy$),
                tap((active: DASHBOARD_TAB) => {
                    this.tabSet.tabs[active].active = true;
                    this.changeDetectorRef.detectChanges();
                }),
            )
            .subscribe();
    }

    private updateNotificationCount(): void {
        this.strings.todaysTitleOnSmartphone = <string>this.translate.instant(
            'DASHBOARD_ASSIGNMENTS_FOR_TODAY_TITLE_SMARTPHONE',
            {
                noOfNotifications: this.notificationService.getTodaysNotifications().length,
            },
        );

        this.strings.upcomingTitleOnSmartphone = <string>this.translate.instant(
            'DASHBOARD_UPCOMING_ASSIGNMENTS_TITLE_SMARTPHONE',
            {
                noOfNotifications: this.notificationService.getUpcomingNotifications().length,
            },
        );

        this.strings.newTitleOnSmartphone = <string>this.translate.instant(
            'DASHBOARD_NEW_ASSIGNMENTS_TITLE_SMARTPHONE',
            {
                noOfNotifications: this.notificationService.getNewNotifications().length,
            },
        );
        this.newAssignmentsCountSubject.next(this.model.newAssignments.length);
    }

    private setActiveTab(isRedirected = false): void {
        const notification = this.notificationService.getNotificationInProgress();
        const { newAssignments, todaysAssignments, upcomingAssignments } = this.model;

        if ((isRedirected || !notification) && newAssignments?.length > 0) {
            this.activeTabSubject.next(DASHBOARD_TAB.NEW);
            return;
        }
        if (notification) {
            const tab = this.notificationService.isTodaysNotification(notification)
                ? DASHBOARD_TAB.TODAYS
                : DASHBOARD_TAB.UPCOMING;
            this.activeTabSubject.next(tab);
            return;
        }

        if (todaysAssignments?.length > 0) {
            this.activeTabSubject.next(DASHBOARD_TAB.TODAYS);
        } else if (upcomingAssignments?.length > 0) {
            this.activeTabSubject.next(DASHBOARD_TAB.UPCOMING);
        } else if (newAssignments?.length > 0) {
            this.activeTabSubject.next(DASHBOARD_TAB.NEW);
        }
    }

    public ngOnDestroy(): void {
        this.model.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
